import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { chakras } from '../data/mock';
import { Sparkles, Zap } from 'lucide-react';

const ChakraSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeChakra, setActiveChakra] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('chakra-section');
    if (section) observer.observe(section);

    return () => observer.disconnect();
  }, []);

  // Vibrant chakra colors
  const chakraColors = {
    'Root': { bg: '#FF6B6B', light: '#FF8A8A' },
    'Sacral': { bg: '#FF9F43', light: '#FFB76B' },
    'Solar Plexus': { bg: '#FFB800', light: '#FFCF4D' },
    'Heart': { bg: '#00C853', light: '#5AFF8A' },
    'Throat': { bg: '#00B4D8', light: '#4DD4F0' },
    'Third Eye': { bg: '#9D4EDD', light: '#B86EF0' },
    'Crown': { bg: '#C77DFF', light: '#D9A8FF' },
  };

  return (
    <section id="chakra-section" className="py-24 px-6 relative overflow-hidden section-pearl">
      {/* Prismatic light effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#FF6B9D]/15 rounded-full blur-3xl animate-pulse-soft" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#9D4EDD]/15 rounded-full blur-3xl animate-pulse-soft" style={{ animationDelay: '1s' }} />
      <div className="absolute top-1/2 left-0 w-80 h-80 bg-[#FFB800]/10 rounded-full blur-3xl animate-pulse-soft" style={{ animationDelay: '2s' }} />
      <div className="absolute top-1/3 right-0 w-72 h-72 bg-[#00B4D8]/15 rounded-full blur-3xl animate-pulse-soft" style={{ animationDelay: '3s' }} />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex items-center justify-center gap-3 mb-6">
            <Zap className="w-5 h-5 text-[#FFB800]" />
            <span className="text-sm uppercase tracking-[0.3em] text-gradient-prism font-bold">
              Energy Centers
            </span>
            <Zap className="w-5 h-5 text-[#FFB800]" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-[#1A1A2E] mb-6">
            Shop by <span className="text-gradient-aurora">Chakra</span>
          </h2>
          <p className="text-lg text-[#4A4A6A] max-w-2xl mx-auto leading-relaxed">
            Each crystal corresponds to specific energy centers in your body. 
            Find the perfect stone to balance and activate your chakras.
          </p>
        </div>

        {/* Chakra Line Visualization */}
        <div className={`relative py-8 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          {/* Chakra points in a flowing line */}
          <div className="flex flex-wrap justify-center items-center gap-4 md:gap-6 lg:gap-8">
            {chakras.map((chakra, index) => {
              const colors = chakraColors[chakra.name];
              return (
                <Link
                  key={chakra.name}
                  to={`/shop?chakra=${chakra.name.toLowerCase().replace(' ', '-')}`}
                  className={`group relative flex flex-col items-center transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                  style={{ transitionDelay: `${index * 100 + 400}ms` }}
                  onMouseEnter={() => setActiveChakra(chakra.name)}
                  onMouseLeave={() => setActiveChakra(null)}
                >
                  {/* Chakra orb */}
                  <div className="relative mb-4">
                    {/* Outer glow */}
                    <div
                      className={`absolute inset-0 rounded-full blur-xl transition-all duration-500 ${
                        activeChakra === chakra.name ? 'opacity-70 scale-[1.8]' : 'opacity-30 scale-100'
                      }`}
                      style={{ backgroundColor: colors.bg }}
                    />
                    {/* Pulsing ring */}
                    <div
                      className={`absolute inset-[-6px] rounded-full border-2 transition-all duration-500 ${
                        activeChakra === chakra.name ? 'opacity-100 scale-125' : 'opacity-0 scale-100'
                      }`}
                      style={{ borderColor: colors.light }}
                    />
                    {/* Main orb */}
                    <div
                      className="relative w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center shadow-xl transform group-hover:scale-110 transition-all duration-300"
                      style={{ 
                        background: `linear-gradient(135deg, ${colors.bg} 0%, ${colors.light} 100%)`,
                        boxShadow: activeChakra === chakra.name 
                          ? `0 8px 30px ${colors.bg}60, 0 0 60px ${colors.bg}40` 
                          : `0 4px 20px ${colors.bg}30`
                      }}
                    >
                      <span className="text-white text-xl md:text-2xl font-serif font-bold drop-shadow-lg">
                        {chakra.name.charAt(0)}
                      </span>
                    </div>
                  </div>

                  {/* Chakra info */}
                  <div className="text-center">
                    <h3 className="font-serif text-[#1A1A2E] text-base md:text-lg mb-1 group-hover:text-[#9D4EDD] transition-colors">
                      {chakra.name}
                    </h3>
                    <p className="text-[10px] md:text-xs text-[#7A7A9A] uppercase tracking-wider">
                      {chakra.description}
                    </p>
                  </div>

                  {/* Hover line indicator */}
                  <div 
                    className={`absolute -bottom-2 w-8 h-1 rounded-full transition-all duration-300 ${
                      activeChakra === chakra.name ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
                    }`}
                    style={{ backgroundColor: colors.bg }}
                  />
                </Link>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className={`text-center mt-16 transition-all duration-1000 delay-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <Link
            to="/chakras"
            className="inline-flex items-center gap-2 bg-white border-2 border-[#9D4EDD] text-[#9D4EDD] px-8 py-4 rounded-xl hover:bg-gradient-to-r hover:from-[#9D4EDD] hover:to-[#FF6B9D] hover:text-white hover:border-transparent hover:shadow-[0_10px_40px_rgba(157,78,221,0.3)] transition-all duration-300 text-sm font-semibold tracking-wider uppercase"
          >
            <Sparkles className="w-4 h-4" />
            Explore Chakra Guide
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ChakraSection;
